import { toast } from 'sonner'
import { createContext, use } from 'react'
import { fError } from '@/utils/format-error'
import { Role, User } from '@/types/user.type'
import axios, { endpoints } from '@/lib/axios'
import { useBoolean } from '@/hooks/use-boolean'
import { useCallback, useEffect, useMemo, useState } from 'react'
import LoadingSection from '@/components/loading-indicator/loading-section'
import ChangePasswordDialog from '@/sections/profile/change-password-dialog.tsx'
import {
  firebaseAuth,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from '@/lib/firebase'

import { AuthContextType } from './type'
import { JWT_ACCESS_TOKEN_KEY } from './constant'
import { isValidToken, setSession } from './utils'

const AuthContext = createContext({} as AuthContextType)

export function AuthProvider({
  children,
  // getUser,
}: {
  children: React.ReactNode
  // getUser: (user: User | null) => void
}) {
  const [user, setUser] = useState<User | null>(null)
  const loading = useBoolean(true)
  const openChangePasswordDialog = useBoolean(false)

  // useDeepEffect(() => {
  //   getUser(user)
  // }, [user])

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(JWT_ACCESS_TOKEN_KEY)
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken)
        const { data } = await axios.get(endpoints.auth.me)
        setUser(data)

        if (data.role === Role.SHOP && data.needChangePassword) {
          openChangePasswordDialog.onTrue()
        }
      } else {
        setUser(null)
        setSession(null)
      }
    } catch (error) {
      console.error('Error during check user session:', error)
      const err = fError(error)
      toast.error(err)
      setUser(null)
      setSession(null)
    } finally {
      loading.onFalse()
    }
  }, [loading])

  useEffect(() => {
    checkUserSession()
  }, [])

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        // 1. Firebase Auth
        const userCredential = await signInWithEmailAndPassword(
          firebaseAuth,
          email,
          password,
        )
        const idToken = await userCredential.user.getIdToken()

        // 2. เรียก backend API เพื่อแลก accessToken
        const { data } = await axios.post(endpoints.auth.login, {
          idToken,
          isRememberMe: true,
        })
        const { accessToken, user } = data
        if (accessToken && user) {
          setUser(user)
          setSession(accessToken)
        }

        if (user.role === Role.SHOP && user.needChangePassword) {
          openChangePasswordDialog.onTrue()
        }
      } catch (error) {
        setUser(null)
        setSession(null)
        throw error
      } finally {
        loading.onFalse()
      }
    },
    [loading],
  )

  const signOut = useCallback(async () => {
    await firebaseSignOut(firebaseAuth)
    setSession(null)
    setUser(null)
    loading.onFalse()
  }, [setUser, loading])

  const checkAuthenticated = user ? 'authenticated' : 'unauthenticated'

  const status = loading.value ? 'loading' : checkAuthenticated

  const memoizedValue = useMemo(
    () => ({
      user,
      signIn,
      signOut,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [user, signIn, signOut, status],
  )

  return (
    <AuthContext value={memoizedValue}>
      <LoadingSection loading={loading.value}>{children}</LoadingSection>
      <ChangePasswordDialog
        open={openChangePasswordDialog.value}
        onOpenChange={openChangePasswordDialog.onFalse}
        disableClose
      />
    </AuthContext>
  )
}

export const useAuthContext = () => {
  const context = use(AuthContext)

  if (!context)
    throw new Error('useAuthContext context must be use inside AuthProvider')

  return context
}
